import { AnalyticsService } from '@services/analytics/analytics.service';
import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { sleep } from '@core/utils';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map
} from 'rxjs/operators';
import { UiService } from '../ui/ui.service';
import { TimeInvokingService } from '../time-invoking/time-invoking.service';
import { WizardService } from '../wizard/wizard.service';

export interface ContentPageModel {
  routeUrl: string;
  viewCode: string;
  code: string;
  stepCode?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ContentService {
  private history: ContentPageModel[] = [];

  private contentPageIndex$ = new BehaviorSubject<number>(-1);

  private routeUrl: string;

  private viewCode: string;

  private fixedDocumentTitle: string;

  constructor(
    private router: Router,
    private analyticsService: AnalyticsService,
    private uiService: UiService,
    private timeInvokingService: TimeInvokingService,
    private wizardService: WizardService
  ) {}

  /**
   * ascolta gli eventi di fine navigazione e imposta il relativo content code
   */
  listenNavigationEnd(): void {
    this.timeInvokingService.invokeOnce(
      'ContentServiceListenNavigationEnd',
      () => {
        this.router.events
          .pipe(
            filter(
              event =>
                event instanceof NavigationEnd && !this.isDummyPage(event.url)
            )
          )
          .subscribe((event: NavigationEnd) => {
            this.updateRouteUrl(event.url);
          });
      }
    );
  }

  /**
   * ascolta i cambiamenti del content page e aggiorna il document title
   */
  listenContentPage(): void {
    this.timeInvokingService.invokeOnce(
      'ContentServiceListenContentPage',
      () => {
        this.getContentPageWhenChange(100).subscribe(async contentPage => {
          const index = this.contentPageIndex$.getValue();
          const prevContentPage = this.history[index - 1];

          if (prevContentPage) {
            this.pushPageExitAnalyticsEvent(prevContentPage);
          }

          this.pushPageViewAnalyticsEvent(contentPage);

          // applico ritardo per dare il tempo all'elemento del page title
          // di essere renderizzato (è il tempo dell'animazione)
          await sleep(600);
          this.updateDocumentTitle();
        });
      }
    );
  }

  /**
   * restituisce osservabile per i cambiamenti del content code
   */
  getContentPageWhenChange(dueTime: number = 5): Observable<ContentPageModel> {
    return this.contentPageIndex$.asObservable().pipe(
      filter(index => index > -1 && !!this.history[index]),
      debounceTime(dueTime),
      distinctUntilChanged(
        (prev, curr) =>
          this.history[prev].code === this.history[curr].code &&
          this.history[prev].stepCode === this.history[curr].stepCode
      ),
      map(index => this.history[index])
    );
  }

  /**
   * imposta il nuovo url
   */
  updateRouteUrl(url: string): void {
    this.routeUrl = url;
    this.viewCode = '';
    this.notifyContentPage();
  }

  /**
   * imposta il nuovo view code a partire dalle section
   */
  updateViewCodeOfSections(data: { count: number; current: number }): void {
    if (data.count <= 1) {
      return;
    }

    if (data.current > 0) {
      this.updateViewCode(`${data.current + 1}`);
    } else {
      this.updateViewCode('');
    }
  }

  /**
   * imposta il nuovo view code
   */
  updateViewCode(code: string): void {
    this.viewCode = code;
    this.notifyContentPage();
  }

  /**
   * notifica il content code
   */
  private async notifyContentPage(): Promise<void> {
    this.history.push({
      viewCode: this.viewCode,
      routeUrl: this.routeUrl,
      code: this.getCode(),
      stepCode: this.wizardService.getActiveStep()?.stepCode
    });

    const index = this.history.length - 1;

    this.contentPageIndex$.next(index);
  }

  /**
   * crea il codice unico derivato da route url e view code
   */
  private getCode(): string {
    let code = '';

    if (this.routeUrl) {
      code += this.routeUrl
        .split('/')
        .filter(x => !!x)
        .join('_');
    }

    if (this.viewCode) {
      code += '_' + this.viewCode;
    }

    if (code.charAt(0) === '_') {
      code = code.substring(1);
    }

    return code;
  }

  /**
   * aggiorna il document title
   */
  private updateDocumentTitle(): void {
    if (!this.fixedDocumentTitle) {
      this.fixedDocumentTitle = document.title;
    }

    document.title = this.createDocumentTitle();
  }

  /**
   * compone il title composto dalla parte fissa e dalla parte dinamica
   */
  private createDocumentTitle(): string {
    const pageTitle = this.getPageTitle();

    return this.fixedDocumentTitle + (!!pageTitle ? ` - ${pageTitle}` : '');
  }

  /**
   * restituisce il titolo della pagina
   */
  private getPageTitle(): string {
    const content = this.uiService.getTopContent();

    const title = content && content.querySelector('.step-header-title');

    if (title) {
      return title.textContent;
    } else {
      return '';
    }
  }

  /**
   * stabilisce se l'url è della pagina dummy
   */
  private isDummyPage(url: string): boolean {
    return url && url.includes('dummy');
  }

  /**
   * aggiunge l'evento di analytics legato al page view
   */
  private pushPageViewAnalyticsEvent(contentPage: ContentPageModel): void {
    let path = contentPage.routeUrl;

    if (!!contentPage.viewCode) {
      path += `-${contentPage.viewCode}`;
    }

    this.analyticsService.pushPageViewEvent({
      path,
      title: document.title,
      step: contentPage.stepCode
    });
  }

  /**
   * aggiunge l'evento di analytics legato al page view
   */
  private pushPageExitAnalyticsEvent(contentPage: ContentPageModel): void {
    let path = contentPage.routeUrl;

    if (!!contentPage.viewCode) {
      path += `-${contentPage.viewCode}`;
    }

    this.analyticsService.pushPageExitEvent({
      path,
      title: document.title,
      step: contentPage.stepCode
    });
  }
}
